import * as THREE from 'three';
import { fbm2, ridge2, worley2, clamp01, lerp, makeRng } from '../util/math.js';

/**
 * Procedural PBR material library.
 *
 * The game ships zero binary assets, so every surface is authored here as
 * canvas-generated albedo / normal / roughness-metal-AO maps. Textures are
 * generated once, cached by key, and shared across every mesh that uses them.
 *
 * Channel packing follows the glTF convention three.js expects when you assign
 * the same texture to roughnessMap/metalnessMap/aoMap:
 *   R = ambient occlusion, G = roughness, B = metalness
 */

const _cache = new Map();
/** Surfaces already reported as unknown, so a per-frame miss logs only once. */
const _warned = new Set();
const _canvasPool = [];

function getCanvas(size) {
  const c = _canvasPool.pop() || document.createElement('canvas');
  c.width = c.height = size;
  return c;
}

/**
 * Build a texture by evaluating `fn(x, y, u, v)` per texel.
 * `fn` writes into the provided [r,g,b,a] 0..255 tuple.
 */
function makeTexture(key, size, fn, { srgb = false, repeat = 1, aniso = 8 } = {}) {
  const cacheKey = `${key}:${size}:${srgb}:${repeat}`;
  if (_cache.has(cacheKey)) return _cache.get(cacheKey);

  const canvas = getCanvas(size);
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const img = ctx.createImageData(size, size);
  const data = img.data;
  const px = [0, 0, 0, 255];

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      px[0] = px[1] = px[2] = 0; px[3] = 255;
      fn(x, y, x / size, y / size, px);
      const i = (y * size + x) * 4;
      data[i] = px[0]; data[i + 1] = px[1]; data[i + 2] = px[2]; data[i + 3] = px[3];
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  tex.anisotropy = aniso;
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  _cache.set(cacheKey, tex);
  return tex;
}

/**
 * Derive a tangent-space normal map from a height function via central
 * differences. `strength` scales the slope; 1 is subtle, 4 is pronounced.
 */
function makeNormalFromHeight(key, size, heightFn, strength = 2, repeat = 1) {
  const step = 1 / size;
  return makeTexture(`${key}#n`, size, (x, y, u, v, px) => {
    const hL = heightFn(u - step, v), hR = heightFn(u + step, v);
    const hD = heightFn(u, v - step), hU = heightFn(u, v + step);
    let nx = (hL - hR) * strength;
    let ny = (hD - hU) * strength;
    const nz = 1;
    const len = Math.hypot(nx, ny, nz);
    px[0] = ((nx / len) * 0.5 + 0.5) * 255;
    px[1] = ((ny / len) * 0.5 + 0.5) * 255;
    px[2] = ((nz / len) * 0.5 + 0.5) * 255;
  }, { repeat });
}

// ---------------------------------------------------------------------------
// Surface height fields — each returns 0..1
// ---------------------------------------------------------------------------

const H = {
  concrete: (u, v) => {
    const base = fbm2(u * 14, v * 14, 5) * 0.55 + fbm2(u * 60, v * 60, 3) * 0.25;
    const pits = worley2(u * 22, v * 22) < 0.12 ? -0.35 : 0;
    const crack = ridge2(u * 6 + 3.1, v * 6, 4) > 0.82 ? -0.45 : 0;
    return clamp01(base + pits + crack + 0.2);
  },
  plaster: (u, v) => clamp01(fbm2(u * 9, v * 9, 4) * 0.5 + fbm2(u * 40, v * 40, 2) * 0.2 + 0.3),
  metalPainted: (u, v) => {
    const chip = worley2(u * 16 + 7, v * 16, 0.9);
    return clamp01(0.6 + (chip < 0.18 ? -0.5 : 0) + fbm2(u * 90, v * 90, 2) * 0.12);
  },
  rust: (u, v) => clamp01(fbm2(u * 11, v * 11, 5) * 0.6 + ridge2(u * 28, v * 28, 3) * 0.4),
  sand: (u, v) => clamp01(worley2(u * 55, v * 55, 1) * 0.4 + fbm2(u * 8, v * 8, 4) * 0.5 + 0.15),
  woodPlank: (u, v) => {
    const plank = Math.floor(v * 7);
    const grain = fbm2(u * 40 + plank * 13, v * 3 + plank * 7, 4);
    const gap = Math.abs((v * 7) % 1 - 0.5) > 0.47 ? -0.5 : 0;
    return clamp01(grain * 0.5 + 0.4 + gap);
  },
  tile: (u, v) => {
    const gx = Math.abs((u * 8) % 1 - 0.5), gy = Math.abs((v * 8) % 1 - 0.5);
    const grout = (gx > 0.45 || gy > 0.45) ? -0.45 : 0;
    return clamp01(0.65 + grout + fbm2(u * 50, v * 50, 3) * 0.1);
  },
  asphalt: (u, v) => clamp01(worley2(u * 70, v * 70, 1) * 0.35 + fbm2(u * 18, v * 18, 5) * 0.5 + 0.1),
  gunMetal: (u, v) => clamp01(0.7 + fbm2(u * 120, v * 6, 3) * 0.2 + (ridge2(u * 200, v * 200, 2) > 0.9 ? -0.3 : 0)),
  fabric: (u, v) => {
    const weave = (Math.sin(u * Math.PI * 180) * Math.sin(v * Math.PI * 180)) * 0.5 + 0.5;
    return clamp01(weave * 0.35 + fbm2(u * 20, v * 20, 3) * 0.4 + 0.25);
  },
  // Awnings, laundry and tarps are single-sided planes viewed from both sides,
  // so they get their own entry purely to carry side: DoubleSide in OVERRIDES.
  fabricDouble: (u, v) => H.fabric(u, v),
  cloth: (u, v) => clamp01(fbm2(u * 26, v * 26, 4) * 0.55 + worley2(u * 12, v * 12) * 0.2 + 0.2),
  sandbag: (u, v) => {
    // Rows of bulging sacks: a stretched cell pattern with a slack weave on top.
    const cell = worley2(u * 6, v * 9, 0.65);
    const weave = (Math.sin(u * Math.PI * 120) * Math.sin(v * Math.PI * 120)) * 0.5 + 0.5;
    return clamp01((1 - cell) * 0.7 + weave * 0.12 + fbm2(u * 30, v * 30, 3) * 0.18);
  },
  dirt: (u, v) => clamp01(fbm2(u * 16, v * 16, 5) * 0.6 + worley2(u * 40, v * 40) * 0.25 + 0.12),
  brick: (u, v) => {
    // Running bond: every other course shifts half a brick.
    const row = Math.floor(v * 16);
    const uu = u * 8 + (row % 2 ? 0.5 : 0);
    const bx = Math.abs((uu % 1) - 0.5), by = Math.abs((v * 16) % 1 - 0.5);
    const mortar = (bx > 0.44 || by > 0.40) ? -0.5 : 0;
    return clamp01(0.68 + mortar + fbm2(u * 60 + row, v * 60, 3) * 0.16);
  },
  rubber: (u, v) => {
    // Moulded tread — directional ribs plus fine matte grain.
    const rib = Math.abs(((v * 24) % 1) - 0.5) < 0.28 ? 0.18 : -0.12;
    return clamp01(0.55 + rib + fbm2(u * 80, v * 80, 2) * 0.14);
  },
  glass: (u, v) => {
    // Nearly flat, with a slight roll and occasional cracks so it catches light.
    const crack = ridge2(u * 5 + 11, v * 5, 3) > 0.88 ? -0.3 : 0;
    return clamp01(0.9 + crack + fbm2(u * 4, v * 4, 2) * 0.06);
  },
};

// ---------------------------------------------------------------------------
// Palette. Kept desaturated and dusty — the grade in PostFX adds the contrast,
// so albedo staying in the 0.05..0.6 range keeps highlights from blowing out.
// ---------------------------------------------------------------------------

// Values are real-world diffuse reflectance, not "what looks right in the
// editor". Dry concrete is ~0.45, sand ~0.45, weathered plaster ~0.58. The
// previous palette sat about a third of a stop under these and then the dirt
// blend dragged mid-tones into the floor, which is what made the whole scene
// read as dusk no matter how the lights were set.
const PAL = {
  concrete: [0.50, 0.49, 0.46],
  plaster: [0.62, 0.58, 0.51],
  metalPainted: [0.28, 0.33, 0.35],
  rust: [0.40, 0.23, 0.14],
  sand: [0.62, 0.53, 0.39],
  woodPlank: [0.38, 0.28, 0.18],
  tile: [0.54, 0.53, 0.50],
  asphalt: [0.24, 0.24, 0.25],
  gunMetal: [0.17, 0.17, 0.18],
  fabric: [0.30, 0.31, 0.25],
  fabricDouble: [0.42, 0.36, 0.28],
  cloth: [0.38, 0.34, 0.30],
  sandbag: [0.42, 0.37, 0.27],
  dirt: [0.26, 0.21, 0.15],
  brick: [0.36, 0.22, 0.16],
  rubber: [0.06, 0.06, 0.07],
  glass: [0.14, 0.17, 0.18],
};

/**
 * Per-surface material overrides applied after construction. These are the
 * properties that cannot be expressed through the packed ORM texture.
 */
const OVERRIDES = {
  fabricDouble: { side: THREE.DoubleSide },
  cloth: { side: THREE.DoubleSide },
  glass: { transparent: true, opacity: 0.28, side: THREE.DoubleSide, depthWrite: false },
};

/**
 * Surfaces that other systems may ask for but that do not warrant their own
 * texture set. Resolved before the lookup so a miss never throws.
 */
const ALIASES = {
  wood: 'woodPlank', plank: 'woodPlank', metal: 'metalPainted', steel: 'gunMetal',
  ground: 'sand', floor: 'concrete', wall: 'plaster', road: 'asphalt',
  flesh: 'fabric', body: 'fabric', tarp: 'fabricDouble', canvas: 'fabricDouble',
};

const SPEC = {
  //            roughBase roughVar metal  aoStrength
  concrete:     [0.92, 0.10, 0.00, 0.55],
  plaster:      [0.88, 0.10, 0.00, 0.45],
  metalPainted: [0.42, 0.30, 0.55, 0.40],
  rust:         [0.86, 0.14, 0.35, 0.60],
  sand:         [0.95, 0.05, 0.00, 0.50],
  woodPlank:    [0.72, 0.18, 0.00, 0.55],
  tile:         [0.35, 0.25, 0.00, 0.45],
  asphalt:      [0.90, 0.10, 0.00, 0.60],
  gunMetal:     [0.34, 0.22, 0.95, 0.35],
  fabric:       [0.94, 0.06, 0.00, 0.50],
  fabricDouble: [0.93, 0.07, 0.00, 0.35],
  cloth:        [0.95, 0.05, 0.00, 0.45],
  sandbag:      [0.96, 0.04, 0.00, 0.65],
  dirt:         [0.97, 0.03, 0.00, 0.60],
  brick:        [0.88, 0.12, 0.00, 0.60],
  rubber:       [0.82, 0.14, 0.00, 0.55],
  glass:        [0.08, 0.06, 0.00, 0.15],
};

function albedoFor(name, size = 512, repeat = 1) {
  const [r, g, b] = PAL[name];
  const h = H[name];
  const rng = makeRng(name.length * 7919);
  const tintU = rng() * 10, tintV = rng() * 10;
  return makeTexture(`${name}#a`, size, (x, y, u, v, px) => {
    const hv = h(u, v);
    // Large-scale tonal drift stops tiling from reading as a repeated stamp.
    const drift = fbm2(u * 2.5 + tintU, v * 2.5 + tintV, 3) * 0.3 + 0.85;
    // Keep the shading range narrow. Wide albedo variation reads as noise, not
    // as surface, and the low end of the old 0.55..1.15 range was doing most of
    // the damage to overall exposure.
    const shade = lerp(0.78, 1.12, hv) * drift;
    // Dirt settles in the crevices (low height) rather than uniformly. It tints
    // toward a dusty brown rather than toward black — real grime is still a
    // diffuse surface with reflectance around 0.2, not a light sink.
    const dirt = clamp01((1 - hv) * 0.6) * 0.22;
    px[0] = clamp01(lerp(r * shade, 0.22, dirt)) * 255;
    px[1] = clamp01(lerp(g * shade, 0.19, dirt)) * 255;
    px[2] = clamp01(lerp(b * shade, 0.15, dirt)) * 255;
  }, { srgb: true, repeat });
}

function ormFor(name, size = 512, repeat = 1) {
  const [rBase, rVar, metal, aoStr] = SPEC[name];
  const h = H[name];
  return makeTexture(`${name}#orm`, size, (x, y, u, v, px) => {
    const hv = h(u, v);
    const ao = lerp(1 - aoStr, 1, clamp01(hv * 0.85 + 0.15));
    const rough = clamp01(rBase + (1 - hv) * rVar - fbm2(u * 30, v * 30, 3) * 0.08);
    // Metal only survives where the surface is proud; recesses read as oxide.
    const m = clamp01(metal * lerp(0.35, 1, hv));
    px[0] = ao * 255; px[1] = rough * 255; px[2] = m * 255;
  }, { repeat });
}

/**
 * Public API. `get(name, opts)` returns a shared MeshStandardMaterial.
 * Pass `{ clone: true }` when a mesh needs to mutate material state at runtime
 * (muzzle heat, damage tinting) so it does not affect every other user.
 */
export const Materials = {
  names: Object.keys(PAL),

  /**
   * Resolve a requested surface to one that actually exists. An unrecognised
   * name falls back to concrete with a one-time warning rather than throwing:
   * a typo in a prop should not abort an entire level build.
   */
  resolve(name) {
    if (PAL[name]) return name;
    const alias = ALIASES[name];
    if (alias && PAL[alias]) return alias;
    if (!_warned.has(name)) {
      _warned.add(name);
      console.warn(`[Materials] unknown surface "${name}" — falling back to concrete`);
    }
    return 'concrete';
  },

  get(rawName, { repeat = 1, size = 512, clone = false, overrides = null } = {}) {
    const name = this.resolve(rawName);
    const key = `${name}:mat:${repeat}:${size}`;
    let mat = _cache.get(key);
    if (!mat) {
      const map = albedoFor(name, size, repeat);
      const orm = ormFor(name, size, repeat);
      const normalMap = makeNormalFromHeight(name, size, H[name], name === 'sand' ? 3 : 2.2, repeat);
      // aoMap is deliberately NOT assigned. three.js samples it from the uv1
      // attribute, and most geometry here (instanced boxes, lathes, extrusions)
      // has only uv — so the AO term collapsed to zero and rendered those
      // surfaces near black. Contact occlusion comes from the post-process AO
      // pass instead, which is the better source anyway since it accounts for
      // neighbouring geometry rather than just the texture's own height field.
      mat = new THREE.MeshStandardMaterial({
        map, normalMap,
        roughnessMap: orm, metalnessMap: orm,
        roughness: 1, metalness: 1,   // scalars multiply the packed map
        envMapIntensity: 1,
        normalScale: new THREE.Vector2(1, 1),
        dithering: true,
      });
      if (OVERRIDES[name]) Object.assign(mat, OVERRIDES[name]);
      mat.name = name;
      _cache.set(key, mat);
    }
    if (clone || overrides) {
      const c = mat.clone();
      if (overrides) Object.assign(c, overrides);
      return c;
    }
    return mat;
  },

  /** Flat emissive material for lamps, screens, tracers. */
  emissive(color, intensity = 4) {
    const key = `emit:${color}:${intensity}`;
    let m = _cache.get(key);
    if (!m) {
      m = new THREE.MeshStandardMaterial({
        color: 0x000000,
        emissive: new THREE.Color(color),
        emissiveIntensity: intensity,
        roughness: 1, metalness: 0,
        toneMapped: true,
      });
      _cache.set(key, m);
    }
    return m;
  },

  /** Height field access for systems that need surface data (footstep audio, decals). */
  height(name, u, v) { return H[name] ? H[name](u, v) : 0.5; },

  dispose() {
    for (const v of _cache.values()) v.dispose?.();
    _cache.clear();
  },
};
